// Replacement is deliberately staged: all incoming identities must be confirmed
// before removing anything. Every mutation uses the validated list version.
const equity = row => ['equity', 'etf', 'stock'].includes(row.assetType)
const membershipId = (row, listId) => row.sources?.find(source => source.kind === 'manual-list' && source.listId === listId)?.membershipId
const checkAbort = signal => { if (signal?.aborted) throw new Error('Stopped. Saved changes remain; check the list before retrying.') }
const changed = () => new Error('The list changed. Check the list and retry the import; no further removals were made.')

async function listVersion(api, listId) {
  const list = (await api.readState()).lists.find(list => list.listId === listId)
  if (!list || !Number.isSafeInteger(Number(list.version))) throw changed()
  return Number(list.version)
}

export async function previewAssetListReplacement({ rows, listId, api, signal }) {
  if (!rows.length || rows.some(row => row.status === 'invalid')) throw new Error('Fix invalid CSV rows before replacing. An empty CSV cannot clear a list.')
  const unique = rows.filter(row => row.status !== 'duplicate')
  if (!unique.length) throw new Error('The CSV has no tickers.')
  const version = await listVersion(api, listId)
  const current = await api.readView({ kind: 'manual-list', listId })
  if (current.some(row => !equity(row) || !row.instrumentId || !membershipId(row, listId))) {
    throw new Error('Replacement currently supports stock/ETF-only lists with verified memberships. Use Add to watchlist for this list.')
  }
  if (new Set(current.map(row => row.symbol)).size !== current.length) throw new Error('The list has ambiguous ticker identities. Resolve them before replacing.')
  const desired = []
  for (const row of unique) {
    checkAbort(signal)
    const response = await api.search(row.symbol, { signal })
    const matches = [...new Map((response.results || []).filter(candidate => candidate.symbol === row.symbol
      && candidate.secType === 'STK' && candidate.exchange === 'SMART' && candidate.currency === 'USD'
      && Number.isSafeInteger(Number(candidate.conid)) && Number(candidate.conid) > 0)
      .map(candidate => [Number(candidate.conid), candidate])).values()]
    if (matches.length !== 1) throw new Error(`${row.symbol}: no unique verified stock/ETF match. Nothing was changed.`)
    desired.push({ ...row, candidate: matches[0], previous: current.find(item => item.symbol === row.symbol) || null })
  }
  checkAbort(signal)
  if (await listVersion(api, listId) !== version) throw changed()
  const wanted = new Set(desired.map(row => row.symbol))
  return { listId, version, desired, current,
    added: desired.filter(row => !row.previous), kept: desired.filter(row => row.previous),
    removed: current.filter(row => !wanted.has(row.symbol)) }
}

export async function applyAssetListReplacement({ plan, api, signal, onRow = () => {}, onRemoval = () => {} }) {
  let version = plan.version
  const wantedIds = new Set()
  checkAbort(signal)
  if (await listVersion(api, plan.listId) !== version) throw changed()
  for (const row of plan.desired) {
    checkAbort(signal)
    onRow({ ...row, status: 'subscribing', message: 'Verifying membership and subscription' })
    let instrumentId, existing = false
    try {
      const result = await api.add(plan.listId, row.candidate, version)
      version = Number(result.listVersion)
      instrumentId = result.instrumentId
      if (!Number.isSafeInteger(version) || !instrumentId) throw new Error('Save confirmation was incomplete. Check the list and retry; old members have not been removed.')
    } catch (error) {
      if (error.code !== 'ASSET_LIST_ALREADY_MEMBER') {
        onRow({ ...row, status: 'error', message: error.message })
        throw error
      }
      // The server confirmed exact-contract membership and durable demand.
      // Only a unique canonical equity row may supply the quote identity.
      const current = await api.readView({ kind: 'manual-list', listId: plan.listId })
      const matches = current.filter(item => equity(item) && item.symbol === row.symbol && item.instrumentId)
      if (matches.length !== 1) throw new Error('Existing instrument identity is ambiguous. No old members were removed.')
      instrumentId = matches[0].instrumentId
      existing = true
    }
    if (row.previous && instrumentId !== row.previous.instrumentId) throw new Error('A retained ticker resolved to a different instrument. No old members were removed.')
    wantedIds.add(instrumentId)
    onRow({ ...row, instrumentId, status: existing ? 'existing' : 'added', message: existing ? 'Kept in list — reused' : 'Added to list' })
  }
  checkAbort(signal)
  if (await listVersion(api, plan.listId) !== version) throw changed()
  const current = await api.readView({ kind: 'manual-list', listId: plan.listId })
  const expected = new Set([...plan.current.map(row => row.instrumentId), ...wantedIds])
  if (current.length !== expected.size || current.some(row => !expected.has(row.instrumentId))) throw changed()
  for (const row of plan.removed) {
    if (wantedIds.has(row.instrumentId) || !current.some(item => item.instrumentId === row.instrumentId
      && membershipId(item, plan.listId) === membershipId(row, plan.listId))) throw changed()
  }
  // Existing RPC retires only this list's demand. The durable reconciler owns
  // provider release, preserving other lists, chart and retained consumers.
  for (const row of plan.removed) {
    checkAbort(signal)
    version = Number(await api.remove(plan.listId, membershipId(row, plan.listId), version))
    if (!Number.isSafeInteger(version)) throw new Error('Removal confirmation was incomplete. Check the list before retrying.')
    onRemoval(row)
  }
  checkAbort(signal)
  const final = await api.readView({ kind: 'manual-list', listId: plan.listId })
  if (await listVersion(api, plan.listId) !== version || final.length !== wantedIds.size
    || final.some(row => !wantedIds.has(row.instrumentId))) throw changed()
  return { added: plan.added.length, kept: plan.kept.length, removed: plan.removed.length }
}
